import { useRef, useState } from 'react';
import { errorMessage, financeGet, useFinanceCodes, type FinanceCtx, type FinanceMember } from '../financeApi';
import ImportReview, { type ReviewRow } from './ImportReview';
import {
  cleanName,
  guessCategory,
  loadExistingKeys,
  matchMember,
  readSheet,
  recordKey,
  toAmount,
  toDateValue,
  type Sheet,
} from './importUtils';

type Mapping = { date: number; name: number; deposit: number; memo: number };

// 은행마다 거래내역 파일의 열 이름이 달라, 흔한 이름으로 먼저 추정하고 사용자가 고칠 수 있게 한다.
// 키워드는 우선순위 순서 - '적요'는 은행에 따라 거래 종류(인터넷·CD)이기도 해서 입금자명 후보 중 가장 뒤에 둔다.
const GUESS: Record<keyof Mapping, string[]> = {
  date: ['거래일시', '거래일자', '거래일', '일자', '일시', '날짜'],
  deposit: ['입금액', '입금금액', '맡기신금액', '입금'],
  name: ['입금자명', '입금자', '보낸분', '보낸사람', '의뢰인', '기재내용', '내용', '적요'],
  memo: ['메모', '비고', '통장표시', '적요'],
};

function guessMapping(headers: string[]): Mapping {
  const used = new Set<number>();
  const compact = headers.map((h) => h.replace(/\s/g, ''));
  const find = (key: keyof Mapping) => {
    for (const keyword of GUESS[key]) {
      const index = compact.findIndex((h, i) => !used.has(i) && h.includes(keyword));
      if (index >= 0) {
        used.add(index);
        return index;
      }
    }
    return -1;
  };
  // 입금자명을 입금액보다 먼저 잡아야 입금액 키워드 '입금'이 '입금자명' 열에 걸리지 않는다.
  const date = find('date');
  const name = find('name');
  const deposit = find('deposit');
  const memo = find('memo');
  return { date, name, deposit, memo };
}

// 통장 입금 내역 매칭: 은행에서 받은 거래내역(엑셀/CSV)의 입금 건을 성도와 매칭해 헌금으로 등록한다.
// 입금자명으로 교적 성도를 자동 매칭하고, 입금자명·적요에 '십일조'·'감사' 같은 말이 있으면 헌금 항목을 추천한다.
export default function BankMatching({ ctx, onDone }: { ctx: FinanceCtx; onDone: () => void }) {
  const { incomeCategories } = useFinanceCodes(ctx);
  const fileRef = useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [defaultCategory, setDefaultCategory] = useState('');
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const fallbackCategory = defaultCategory || incomeCategories[0] || '';

  const onFile = async (file: File | undefined) => {
    setError(null);
    setDone(null);
    setRows(null);
    setSheet(null);
    if (!file) return;
    try {
      const read = await readSheet(file);
      if (read.headers.length === 0 || read.rows.length === 0) {
        setError('거래 내역을 찾지 못했습니다.');
        return;
      }
      setSheet(read);
      setMapping(guessMapping(read.headers));
    } catch (err) {
      setError('파일을 읽지 못했습니다. 은행에서 받은 엑셀(.xlsx, .xls) 또는 CSV 파일인지 확인해 주세요.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const buildRows = async () => {
    if (!sheet || !mapping) return;
    if (mapping.date < 0 || mapping.name < 0 || mapping.deposit < 0) {
      setError('거래일·입금자명·입금액 열을 모두 골라 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const members = (await financeGet<FinanceMember[]>(ctx, '/members')) ?? [];
      const deposits = sheet.rows
        .map((cells) => ({ cells, amount: toAmount(cells[mapping.deposit]) }))
        .filter(({ amount }) => amount > 0);
      if (deposits.length === 0) {
        setError('입금액이 있는 거래가 없습니다. 입금액 열을 다시 확인해 주세요.');
        return;
      }
      if (deposits.length > 3000) {
        setError('한 번에 3,000건까지 처리할 수 있습니다. 기간을 나눠 받아 주세요.');
        return;
      }
      const parsed = deposits.map(({ cells, amount }, i) => {
        const rawName = String(cells[mapping.name] ?? '').trim();
        const memoText = mapping.memo >= 0 ? String(cells[mapping.memo] ?? '').trim() : '';
        const { match, candidates } = matchMember(rawName, members);
        const row: ReviewRow = {
          key: i,
          include: true,
          recordDate: toDateValue(cells[mapping.date]),
          rawName,
          match,
          candidates,
          category: guessCategory(`${rawName} ${memoText}`, incomeCategories, fallbackCategory),
          amount,
          memo: memoText || `입금자 ${rawName}`,
          method: '계좌이체',
          duplicate: false,
        };
        return row;
      });
      const existing = await loadExistingKeys(ctx, parsed.map((r) => r.recordDate));
      setRows(parsed.map((r) => {
        const duplicate = existing.has(recordKey(r.recordDate, r.amount, r.match.kind === 'member' ? r.match.name : cleanName(r.rawName)));
        return { ...r, duplicate, include: !duplicate };
      }));
    } catch (err) {
      setError(errorMessage(err, '매칭하지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const columnSelect = (key: keyof Mapping, label: string, optional = false) =>
    sheet && mapping && (
      <label className="manage-field">
        <span>{label}</span>
        <select value={mapping[key]} onChange={(e) => setMapping({ ...mapping, [key]: Number(e.target.value) })}>
          <option value={-1}>{optional ? '사용 안 함' : '선택'}</option>
          {sheet.headers.map((h, i) => <option key={i} value={i}>{h || `${i + 1}번째 열`}</option>)}
        </select>
      </label>
    );

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">통장 입금 매칭</h2>
          <p className="manage-panel-desc">은행 거래내역 파일의 입금 건을 성도와 매칭해 헌금으로 등록합니다. 출금 건은 자동으로 제외됩니다.</p>
        </div>
      </div>

      {done !== null && (
        <div className="fin-inline-ok">
          {done}건을 헌금으로 등록했습니다. <button type="button" className="fin-linkbtn" onClick={onDone}>헌금 내역 보기 →</button>
        </div>
      )}

      {!rows && (
        <div className="fin-steps">
          <div className="fin-step">
            <b>1. 거래내역 파일 올리기</b>
            <p>인터넷뱅킹에서 헌금 계좌의 거래내역을 엑셀로 내려받아 올려 주세요. 파일은 서버에 저장되지 않고 브라우저에서만 읽습니다.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => onFile(e.target.files?.[0])} />
            <button type="button" className="manage-btn manage-btn--primary" onClick={() => fileRef.current?.click()}>거래내역 파일 선택</button>
          </div>
          {sheet && mapping && (
            <div className="fin-step">
              <b>2. 열 확인 ({sheet.rows.length}줄)</b>
              <div className="manage-form manage-form--grid">
                {columnSelect('date', '거래일')}
                {columnSelect('name', '입금자명')}
                {columnSelect('deposit', '입금액')}
                {columnSelect('memo', '메모·적요', true)}
                <label className="manage-field">
                  <span>기본 헌금 항목</span>
                  <select value={fallbackCategory} onChange={(e) => setDefaultCategory(e.target.value)}>
                    {incomeCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <button type="button" className="manage-btn manage-btn--primary" disabled={busy} onClick={buildRows}>
                {busy ? '매칭 중...' : '성도 매칭하기'}
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div className="fin-inline-error">{error}</div>}

      {rows && (
        <ImportReview
          ctx={ctx}
          initialRows={rows}
          categories={incomeCategories}
          source="bank"
          onCancel={() => setRows(null)}
          onSaved={(count) => {
            setRows(null);
            setSheet(null);
            setDone(count);
          }}
        />
      )}
    </div>
  );
}
